<template>
    <div>
        <h5>{{ this.$t(this.graphTitle, { start_year: this.linearWindowsYears[0].toString(), end_year: this.linearWindowsYears[this.linearWindowsYears.length - 1].toString() }) }}</h5>
        <line-graph
            :chart-data="linearChartData"
            :chart-options="linearChartOptions"
        />
    </div>
</template>

<script>
import LineGraph from '@/components/LineGraph';
export default {
    props: {
        calculatedMonthlyData: Object,
        selectedYear: String,
        linearWindowsYears: Array,
        graphTitle: String,
    },
    data() {
        return {
        }
    },
    name: 'LinearRegression',
    components: {
        LineGraph,
    },
    computed: {
        linearChartData() {
            if (this.isDataLoading) {
                return {
                    labels: [],
                    datasets: []
                };
            }
            return {
                labels: this.linearX,

                datasets: [ {
                    label: this.$t('linear-regression', { start_year: this.linearWindowsYears[0].toString(), end_year: this.linearWindowsYears[this.linearWindowsYears.length - 1].toString() }),
                    data: this.linearRegressionData,
                    type: 'line',
                    borderColor: 'rgba(212,81,81,1)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    tension: 0.1,
                    borderWidth: 2,
                    pointRadius: 0
                }, {
                    label: this.$t('volume'),
                    data: this.linearData,
                    type: 'line',
                    borderColor: 'rgba(79,137,210, 1)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    tension: 0.1,
                    borderWidth: 2,
                    pointRadius: 0
                }]
            };
        },  
        linearChartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        title: {
                            display: true,
                            text: this.$t('volume')
                        },
                        beginAtZero: true,
                    },
                    x: {
                        title: {
                            display: true,
                            text: this.$t('month')
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'top'
                    }
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                },
                hover: {
                    mode: 'index',
                    intersect: false,
                },
                animation: {
                    duration: 1000,
                },
            };
        },
        isDataLoading() {
            return !this.calculatedMonthlyData || !this.calculatedMonthlyData[this.selectedYear];
        },

        linearData() {
            if (this.isDataLoading) {
                return [];
            }
            const y = this.linearWindowsYears.map(year => this.calculatedMonthlyData[year]).flat();
            y.forEach((value,index) => {
                //this will set any null values to 0 if the next value is not null - this is to avoid the line graph from having a gap in the data,
                //null at the end will be filtered out so as not to affect the linear regression calculation
                //a month with zero real data may be falsely interpreted as null if it's at the end of the current year's data unfortunately.
                if (index < y.length - 1 && value === null && y[index + 1] !== null) {
                    y[index] = 0;
                }
            });
            return y.filter(value => value !== null);
        },

        linearRegressionData() {
            if (this.isDataLoading) {
                return [];
            }
            const { slope, intercept } = this.linearRegression;
            const predictedY = [];
            for (let i = 0; i < this.linearData.length; i++) {
                predictedY.push(Math.round(1000 * (slope * (i + 1) + intercept)) / 1000);
            }
            return predictedY;
        },

        linearRegression() {
            if (this.isDataLoading) {
                return null;
            }
            const y = this.linearData;
            const n = y.length;
            const x = Array.from({ length: n }, (_, i) => i + 1);

            let sum_x = 0;
            let sum_y = 0;
            let sum_xy = 0;
            let sum_xx = 0;

            // Calculate the sums needed for the formula
            for (let i = 0; i < n; i++) {
                sum_x += x[i];
                sum_y += y[i];
                sum_xy += (x[i] * y[i]);
                sum_xx += (x[i] * x[i]);
            }

            // Calculate the slope (b)
            const slope = (n * sum_xy - sum_x * sum_y) / (n * sum_xx - sum_x * sum_x);

            // Calculate the intercept (a)
            const intercept = (sum_y - slope * sum_x) / n;

            return { slope, intercept };
        },

        linearX() {
            if (this.isDataLoading) {
                return [];
            }
            // Flatten all months for all years, but filter out nulls
            return this.linearWindowsYears
                .map(year =>
                    this.calculatedMonthlyData[year]
                        .map((value, index) => {
                            if (value === null) return null;
                            const date = new Date(year, index, 1);
                            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
                        })
                        .filter(val => val !== null)
                )
                .flat();
        },
    },

    methods: {
    }
}
</script>